// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { Appointment, ChargeItem, Encounter, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM,
  CHARGE_ITEM_SOURCE_SELF_PAY,
  CHARGE_ITEM_SOURCE_SYSTEM,
} from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type PatientChargeStatus = 'paid' | 'pending' | 'overdue' | 'cancelled';

/**
 * `ChargeItem` carries no FHIR-standard "due date" field, so we stash the
 * payment due date on a Polaris extension. The dashboard reads it; absence
 * means no due date set yet (rendered as "—" with no overdue computation).
 */
export const CHARGE_DUE_DATE_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/charge-due-date';
export const INSURANCE_CONTRIBUTION_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/insurance-contribution';

/**
 * `ChargeItem.identifier` from a Polaris-issued invoice. Falls back to a
 * derived "POL-{id-short}" string when no identifier was assigned at creation.
 */
export const POLARIS_INVOICE_IDENTIFIER_SYSTEM = 'https://polarissleep.com/invoices';

export interface PatientCharge {
  id: string;
  charge: ChargeItem;
  invoiceNumber: string;
  purpose: string;
  insuranceContribution: number | undefined;
  estimatedPatientContribution: number | undefined;
  actualPatientContribution: number | undefined;
  balanceDue: number;
  dueDateISO: string | undefined;
  status: PatientChargeStatus;
  visitSummary?: string;
  selfPay?: boolean;
}

function getInvoiceNumber(charge: ChargeItem): string {
  const polarisId = charge.identifier?.find((i) => i.system === POLARIS_INVOICE_IDENTIFIER_SYSTEM)?.value;
  if (polarisId) {
    return polarisId;
  }
  // Derive a stable POL-### display from the resource id when no real invoice
  // number is set yet, so the column never renders blank.
  return `POL-${(charge.id ?? '').slice(-7).toUpperCase()}`;
}

function getDueDate(charge: ChargeItem): string | undefined {
  return charge.extension?.find((ext) => ext.url === CHARGE_DUE_DATE_EXTENSION_URL)?.valueDateTime;
}

export function getPatientChargeStatus(charge: ChargeItem, dueDateISO: string | undefined): PatientChargeStatus {
  if (charge.status === 'billed') {
    return 'paid';
  }
  if (charge.status === 'aborted') {
    return 'cancelled';
  }
  if (dueDateISO && new Date(dueDateISO).getTime() < Date.now()) {
    return 'overdue';
  }
  return 'pending';
}

export function isSelfPayCharge(charge: ChargeItem): boolean {
  return (
    charge.meta?.tag?.some(
      (tag) => tag.system === CHARGE_ITEM_SOURCE_SYSTEM && tag.code === CHARGE_ITEM_SOURCE_SELF_PAY
    ) ?? false
  );
}

async function providerLabel(medplum: MedplumClient, actor: Reference | undefined): Promise<string | undefined> {
  if (actor?.display) {
    return actor.display;
  }
  if (!actor?.reference?.startsWith('Practitioner/')) {
    return undefined;
  }
  try {
    const practitioner = await medplum.readResource('Practitioner', actor.reference.slice('Practitioner/'.length));
    const name = practitioner.name?.[0];
    return [name?.given?.join(' '), name?.family].filter(Boolean).join(' ') || undefined;
  } catch {
    return undefined;
  }
}

function buildCharge(charge: ChargeItem, visitSummary?: string): PatientCharge {
  const dueDateISO = getDueDate(charge);
  const actualPatientContribution = charge.priceOverride?.value;
  const insuranceContribution = charge.extension?.find(
    (extension) => extension.url === INSURANCE_CONTRIBUTION_EXTENSION_URL
  )?.valueMoney?.value;
  return {
    id: charge.id as string,
    charge,
    invoiceNumber: getInvoiceNumber(charge),
    purpose: charge.code?.coding?.[0]?.display ?? charge.code?.text ?? '—',
    insuranceContribution,
    estimatedPatientContribution: undefined,
    actualPatientContribution,
    balanceDue: charge.status === 'billed' || charge.status === 'aborted' ? 0 : (actualPatientContribution ?? 0),
    dueDateISO,
    status: getPatientChargeStatus(charge, dueDateISO),
    visitSummary,
    selfPay: isSelfPayCharge(charge),
  };
}

export interface UsePatientChargesReturn {
  charges: PatientCharge[];
  overdueCount: number;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Loads the signed-in patient's `ChargeItem`s for the dashboard's "History of
 * Charges" table. Computes a richer per-row payment status (paid / pending /
 * overdue) than `ChargeItem.status` provides on its own.
 */
export function usePatientCharges(): UsePatientChargesReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [visitSummaries, setVisitSummaries] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCharges = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const items: ChargeItem[] = [];
      for await (const page of medplum.searchResourcePages('ChargeItem', {
        subject: `Patient/${profile.id}`,
        _sort: '-occurrence',
        _count: '100',
      })) {
        items.push(...page);
      }
      setChargeItems(items);
      const summaries: Record<string, string> = {};
      for (const charge of items) {
        if (!charge.id || !isSelfPayCharge(charge) || !['planned', 'billable'].includes(charge.status ?? '')) {
          continue;
        }
        try {
          const appointmentId =
            charge.identifier?.find((identifier) => identifier.system === APPOINTMENT_CHARGE_APPOINTMENT_SYSTEM)
              ?.value ??
            charge.service?.find((service) => service.reference?.startsWith('Appointment/'))?.reference?.slice(12);
          if (appointmentId) {
            const appointment = (await medplum.readResource('Appointment', appointmentId)) as Appointment;
            const type = appointment.appointmentType?.text ?? charge.code?.text ?? 'Visit';
            const date = (appointment.start ?? charge.occurrenceDateTime)?.slice(0, 10) ?? 'date unavailable';
            const provider = await providerLabel(
              medplum,
              appointment.participant?.find((part) => part.actor?.reference?.startsWith('Practitioner/'))?.actor
            );
            summaries[charge.id] = [type, date, provider].filter(Boolean).join(' · ');
          } else if (charge.context?.reference?.startsWith('Encounter/')) {
            const encounter = (await medplum.readResource(
              'Encounter',
              charge.context.reference.slice(10)
            )) as Encounter;
            const type = encounter.type?.[0]?.text ?? charge.code?.text ?? 'Visit';
            const date = (encounter.period?.start ?? charge.occurrenceDateTime)?.slice(0, 10) ?? 'date unavailable';
            const provider = await providerLabel(
              medplum,
              encounter.participant?.find((part) => part.individual?.reference?.startsWith('Practitioner/'))?.individual
            );
            summaries[charge.id] = [type, date, provider].filter(Boolean).join(' · ');
          }
        } catch {
          // A charge remains payable even if its historical visit is unavailable.
        }
      }
      setVisitSummaries(summaries);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load charges'));
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    fetchCharges().catch(console.error);
  }, [fetchCharges]);

  const charges = useMemo(
    () => chargeItems.map((charge) => buildCharge(charge, charge.id ? visitSummaries[charge.id] : undefined)),
    [chargeItems, visitSummaries]
  );
  const overdueCount = useMemo(() => charges.filter((c) => c.status === 'overdue').length, [charges]);

  return { charges, overdueCount, loading, error, refresh: fetchCharges };
}
